'use client';

import { useState, useEffect } from 'react';
import { QueryClient, QueryClientProvider, MutationCache, type Query } from '@tanstack/react-query';
import { toastBus } from '@/core/lib/toast-bus';
import {
  hydrateQueryCache,
  debouncedPersist,
  cancelPendingPersist,
  type TanStackQueryState,
} from '@/core/lib/query-persister';

function getErrorMessage(error: unknown): string {
  if (error && typeof error === 'object') {
    const e = error as Record<string, unknown>;
    if (typeof e.message === 'string') return e.message;
    if (e.error && typeof e.error === 'object') {
      const inner = e.error as Record<string, unknown>;
      if (typeof inner.message === 'string') return inner.message;
    }
  }
  return 'An unexpected error occurred';
}

function snapshotQuery(query: Query): TanStackQueryState | null {
  if (!query.queryHash) return null;
  return {
    queryHash: query.queryHash,
    queryKey: query.queryKey as unknown[],
    data: query.state.data,
    dataUpdatedAt: query.state.dataUpdatedAt,
    status: query.state.status as 'success' | 'error' | 'pending',
    error: query.state.error,
  };
}

export function QueryProvider({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 5 * 60 * 1000,
            retry: 1,
            retryDelay: 1000,
            refetchOnWindowFocus: false,
          },
          mutations: {
            retry: 0,
          },
        },
        mutationCache: new MutationCache({
          // Fallback feedback for mutations whose caller shows none. A caller
          // that raises its own toast sets `meta: { toast: 'self' }`; without
          // that, both fired and every such action showed two toasts.
          onSuccess: (_data, _variables, _context, mutation) => {
            const meta = mutation.options.meta;
            if (meta?.toast === 'self') return;
            toastBus.emit('success', meta?.successMessage ?? 'Success');
          },
          onError: (error, _variables, _context, mutation) => {
            if (mutation.options.meta?.toast === 'self') return;
            toastBus.emit('error', 'Request Failed', getErrorMessage(error));
          },
        }),
      })
  );

  // Hydrate persisted queries once at startup
  useEffect(() => {
    let cancelled = false;
    hydrateQueryCache()
      .then((entries) => {
        if (cancelled || entries.length === 0) return;
        for (const entry of entries) {
          // Hydration resolves asynchronously, often AFTER the page's own
          // fetches have answered. A persisted snapshot must never replace
          // newer server data — that resurrected a just-deleted pricing rule
          // on reload. Only fill a query that is empty or older.
          const current = queryClient.getQueryState(entry.queryKey);
          if (current && current.dataUpdatedAt >= entry.dataUpdatedAt) continue;
          queryClient.setQueryData(entry.queryKey, entry.data, {
            updatedAt: entry.dataUpdatedAt,
          });
          // The snapshot is written on a debounce, so it can predate the last
          // write (a delete followed by a navigation). Show it for an instant
          // paint, but mark it stale so the server is always asked once —
          // otherwise a recent snapshot counts as "fresh" and is never refetched.
          void queryClient.invalidateQueries({ queryKey: entry.queryKey, exact: true });
        }
      })
      .catch(() => {
        // Hydration is best-effort — fall through to network
      });
    return () => {
      cancelled = true;
    };
  }, [queryClient]);

  // Subscribe to cache mutations and persist (debounced, event-driven)
  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.type === 'added' || event.type === 'updated' || event.type === 'removed') {
        const cache = queryClient.getQueryCache().getAll();
        const snapshot = cache
          .map(snapshotQuery)
          .filter((s): s is TanStackQueryState => s !== null);
        debouncedPersist(() => snapshot);
      }
    });
    return () => {
      unsubscribe();
      cancelPendingPersist();
    };
  }, [queryClient]);

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}
